import { useCallback, useEffect, useMemo, useState } from 'react'
import { useSearchParams } from 'react-router-dom'
import { DocumentTabPane } from '.'
import {
  closeUploadModal,
  openUploadModal,
  useFeatureDispatch,
  useFeatureState,
} from '../lib/state'
import {
  DescriptionType,
  TabCategoryKeys as ModalTypes,
} from '../lib/constants'

import { UploadDocumentType } from '@/constants/businessConstants'
import { useDocumentUpload } from '@/components/documents/upload/hooks/useDocumentUpload'
import { DocumentCategorySelect } from '@/components/form-fields'
import { fetchUserDocuments } from '../lib/userDocuments.slice'
import { useAppDispatch } from '@/utils/typeHelpers'
import { RowTypes } from '../table/TableRows'
import { UserDocumentsTable } from '../table'
import { DocumentSearchBarOnChangeData } from '../shared/DocumentsSearchBar'
import { TabPaneToolbar } from '../shared/TabPaneToolbar'
import { UploadDocumentButton } from '../shared/UploadDocumentButton'
import { useAnalyticsTrack } from '@/features/amplitude'
import { DocumentUploadDialog, Text } from '@/ui'
import { selectUserDocumentCategoryByDocument } from '../lib/userDocuments.selector'
import { useReselector } from '@/utils/sharedHooks'

const COLUMNS = [
  {
    sortBy: 'fileName',
    title: 'File Name',
  },
  {
    sortBy: 'description',
    title: 'Description',
  },
  {
    sortBy: 'tag',
    title: 'Document Type',
  },
  {
    sortBy: null,
    title: 'Actions',
  },
]

export const Bookkeeping: DocumentTabPane = ({ tableOptions, documents }) => {
  const dispatch = useFeatureDispatch()
  const appDispatch = useAppDispatch()
  const [filteredDocuments, setFilteredDocuments] = useState(documents)
  const [isRefreshing, setIsRefreshing] = useState(false)
  const featureState = useFeatureState()
  const { activeUploadModal, userDocumentCategoryId, selectedDocument } =
    featureState
  const isRefreshingAfterDelete = featureState.isRefreshingAfterDelete ?? false
  const [searchParams] = useSearchParams()
  const track = useAnalyticsTrack()
  const documentCategoryId = searchParams.get('documentCategoryId')
  const [localUserDocumentCategoryId, setLocalUserDocumentCategoryId] =
    useState<number | null | undefined>(
      documentCategoryId ? Number(documentCategoryId) : userDocumentCategoryId
    )
  const category = useReselector(
    selectUserDocumentCategoryByDocument,
    selectedDocument || null
  )

  useEffect(() => {
    const documentCategoryId = searchParams.get('documentCategoryId')
    const descriptionType = searchParams.get('descriptionType')

    // Type guard to ensure descriptionType is valid
    if (documentCategoryId && descriptionType) {
      const isValidDescriptionType = (
        type: string
      ): type is DescriptionType => {
        return Object.values(DescriptionType).includes(type as DescriptionType)
      }

      if (isValidDescriptionType(descriptionType)) {
        dispatch?.(
          openUploadModal(
            ModalTypes.BOOKKEEPING,
            parseInt(documentCategoryId),
            descriptionType
          )
        )
      }
    }
  }, [searchParams, dispatch])

  useEffect(() => {
    if (!isRefreshing && !isRefreshingAfterDelete) {
      setFilteredDocuments(documents)
    } else if (isRefreshingAfterDelete) {
      // Clear table when deleting
      setFilteredDocuments([])
    }
  }, [documents, isRefreshing, isRefreshingAfterDelete])

  const cardDescription = useMemo(() => {
    return selectedDocument && category?.description
      ? category.description
      : 'Share bookkeeping-related documents here.'
  }, [selectedDocument, category])

  const onSearch = useCallback((data: DocumentSearchBarOnChangeData) => {
    setFilteredDocuments(data.filtered)
  }, [])

  const { uploadDocuments, isUploading } = useDocumentUpload({
    documentCategory: 'bookkeeping',
    documentType: UploadDocumentType.BOOKKEEPING,
    userDocumentCategoryId:
      selectedDocument?.userDocumentCategoryId || localUserDocumentCategoryId,
    extraPayload: selectedDocument?.userDocumentCategoryId
      ? {
          userDocumentCategoryId: selectedDocument.userDocumentCategoryId,
        }
      : undefined,
    onUploadSuccess: async (_uploadedDocuments) => {
      // Clear the table immediately
      setIsRefreshing(true)
      setFilteredDocuments([])

      // Close modal
      dispatch?.(closeUploadModal())

      // Fetch fresh data
      await appDispatch(fetchUserDocuments(true, true))

      // Re-enable updates from props
      setIsRefreshing(false)
    },
    onUploadError: (errorMessage) => {
      console.error('Upload failed:', errorMessage)
    },
  })

  const handleDocumentsUpload = useCallback(
    async (documents: File[]) => {
      await uploadDocuments(documents)
    },
    [uploadDocuments]
  )

  const openModal = () => {
    track('clicked document upload', {
      document_category: ModalTypes.BOOKKEEPING,
      document_subcategory: '',
    })
    dispatch?.(openUploadModal(ModalTypes.BOOKKEEPING))
  }

  return (
    <>
      <TabPaneToolbar
        documents={documents}
        onSearch={onSearch}
        rightElement={<UploadDocumentButton onClick={openModal} />}
      >
        <Text>Share bookkeeping-related documents here.</Text>
      </TabPaneToolbar>

      <UserDocumentsTable
        columns={COLUMNS}
        documents={filteredDocuments}
        rowType={RowTypes.BOOKKEEPING}
        tableOptions={tableOptions}
      />

      <DocumentUploadDialog
        isOpen={activeUploadModal === ModalTypes.BOOKKEEPING}
        onClose={() => dispatch?.(closeUploadModal())}
        onOpenChange={(open) => {
          if (!open) {
            dispatch?.(closeUploadModal())
          }
        }}
        onUpload={handleDocumentsUpload}
        isUploadDisabled={isUploading}
        cardTitle={
          selectedDocument && category
            ? category.title
            : 'Upload Bookkeeping Documents'
        }
        cardDescription={cardDescription}
      >
        {!selectedDocument && (
          <DocumentCategorySelect
            userDocumentCategoryId={localUserDocumentCategoryId}
            setUserDocumentCategoryId={setLocalUserDocumentCategoryId}
            documentCategoryType={UploadDocumentType.BOOKKEEPING}
          />
        )}
      </DocumentUploadDialog>
    </>
  )
}

Bookkeeping.paneDescription =
  'Please use this section to upload any bookkeeping-specific documents.'
Bookkeeping.paneTitle = 'Bookkeeping'
